import { FusionAuthClient } from '@fusionauth/typescript-client';
import { BaseAuth, AuthResult, AuthError } from './base-auth';
import { AUTH_CONFIG } from './auth0-variables';
import CONFIG from 'Config';
import { Utils } from '../utils';

export class FusionAuthAuth extends BaseAuth {
  private fusionAuthClient: FusionAuthClient;
  private applicationId: string;

  constructor() {
    super();
    const fusionAuthConfig = AUTH_CONFIG.fusionAuth || {
      serverUrl: 'https://enflux-dev.fusionauth.io',
      applicationId: AUTH_CONFIG.clientId,
      apiKey: ''
    };

    this.fusionAuthClient = new FusionAuthClient(
      fusionAuthConfig.apiKey || '',
      fusionAuthConfig.serverUrl
    );
    this.applicationId = fusionAuthConfig.applicationId;
  }

  login = async (username: string, password: string, onError: (err: AuthError) => void) => {
    try {
      this.EAIClient.logLogin(username);
      const response = await this.fusionAuthClient.login({
        loginId: username,
        password: password,
        applicationId: this.applicationId,
        metaData: {
            scopes: ['profile']
        }
      }).catch((error) => {
        onError({
          error: 'login_failed',
          description: error.message || 'Wrong email or password'
        });
      });
      if (response) {
        if (response?.wasSuccessful()) {
          const loginResponse = response.response;
          if (loginResponse.token) {
            const authResult: AuthResult = {
              idToken: loginResponse.token,
              accessToken: loginResponse.token,
              email: username,
              expiresIn: loginResponse.tokenExpirationInstant ? 
                Math.floor((loginResponse.tokenExpirationInstant - Date.now()) / 1000) : undefined
            };
          //   return;
            this.handleAuthSuccess(authResult);
          } else {
            onError({
              error: 'no_token',
              errorDescription: 'No token received from FusionAuth'
            });
          }
        } else {
          this.EAIClient.logBadLogin(username);
        }
      }
    } catch (error: any) {
      this.EAIClient.logBadLogin(username);
      if (error?.response?.status === 404) {
        onError({
          error: 'login_failed',
          errorDescription: 'Wrong email or password'
        });
      } else {
        onError({
          error: 'login_error',
          errorDescription: error.message || 'Login error occurred'
        });
      }
    }
  };

  loginRedirect() {
    const postRedirect = localStorage.getItem("post_login_redirect");
    if (location.pathname !== "/callback" && !location.pathname.includes("/login") && location.pathname !== postRedirect) {
      localStorage.setItem("post_login_redirect", `${location.pathname}${location.search}`);
    }
    if (location.pathname !== "/login" && location.pathname !== "/callback" && location.pathname !== "/activate" && location.pathname !== "clearstorage") {
      window.location.href = CONFIG.paths.base + "login";
    }
  }

  authorizeSSOConnection = (connection: string) => {
    // FusionAuth SSO implementation would go here
    // This would typically redirect to FusionAuth's OAuth endpoint
    const fusionAuthConfig = AUTH_CONFIG.fusionAuth || {
      serverUrl: 'https://fusionauth.local.enflux.com',
      applicationId: AUTH_CONFIG.clientId,
      apiKey: ''
    };
    
    const fusionAuthUrl = `${fusionAuthConfig.serverUrl}/oauth2/authorize?` +
      `client_id=${this.applicationId}&` +
      `response_type=code&` +
      `redirect_uri=${encodeURIComponent(`${location.protocol}//${location.host}/callback`)}&` +
      `scope=email%20profile` +
      `&connection=${connection}`;
    
    window.location.href = fusionAuthUrl;
  };

  resetPassword = async (email: string, onError: (err: AuthError) => void, onSuccess: (resp: any) => void) => {
    try {
      const response = await this.fusionAuthClient.forgotPassword({
        applicationId: this.applicationId,
        loginId: email
      });

      if (response.wasSuccessful()) {
        onSuccess({ message: 'Password reset email sent' });
      } else {
        const errorResponse = response.exception;
        onError({
          error: 'reset_failed',
          errorDescription: errorResponse?.message || 'Password reset failed'
        });
      }
    } catch (error: any) {
      onError({
        error: 'reset_error',
        errorDescription: error.message || 'Password reset error occurred'
      });
    }
  };

  handleAuthentication = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (error) {
      this.handleAuthError({
        error: error,
        errorDescription: errorDescription || 'OAuth error'
      });
      return;
    }

    if (code) {
      this.handleOAuthCallback(code);
    } else {
      // Check if we have a token in localStorage (for direct token authentication)
      const token = localStorage.getItem('fusionauth_token');
      if (token) {
        this.handleAuthSuccess({
          idToken: token,
          accessToken: token
        });
      } else {
        this.handleAuthError({
          error: 'no_code',
          errorDescription: 'No authorization code or token found'
        });
      }
    }
  };

  private handleOAuthCallback = async (code: string) => {
    try {
      const response = await this.fusionAuthClient.exchangeOAuthCodeForAccessToken(
        code, 
        this.applicationId,
        '', // client_secret - empty for public clients
        `${location.protocol}//${location.host}/callback`
      );

      if (response.wasSuccessful()) {
        const tokenResponse = response.response;
        if (tokenResponse.access_token) {
          const authResult: AuthResult = {
            idToken: tokenResponse.access_token,
            accessToken: tokenResponse.access_token,
            expiresIn: tokenResponse.expires_in,
            tokenType: tokenResponse.token_type
          };
          this.handleAuthSuccess(authResult);
        } else {
          this.handleAuthError({
            error: 'no_token',
            errorDescription: 'No access token received'
          });
        }
      } else {
        const errorResponse = response.exception;
        this.handleAuthError({
          error: 'token_exchange_failed',
          errorDescription: errorResponse?.message || 'Token exchange failed'
        });
      }
    } catch (error: any) {
      this.handleAuthError({
        error: 'callback_error',
        errorDescription: error.message || 'OAuth callback error'
      });
    }
  };

  handleLogout = (callback: () => void) => {
    this.EAIClient.logOut().then(() => {
      Utils.clearLocalstorage();
      // Clear FusionAuth specific tokens
      localStorage.removeItem('fusionauth_token');
      callback();
    });
  };
} 